import type { LichessGame } from '../services/lichessApi';
import type { ChessPlayerColor } from './chessPgnMeta';

export type LichessGameOutcome = 'win' | 'loss' | 'draw';

export type LichessGameSummary = {
  gameId: string;
  dateIso: string;
  opponent: string;
  outcome: LichessGameOutcome;
  outcomeLabel: string;
  speedLabel: string;
  myColor: ChessPlayerColor | null;
  listLabel: string;
  headline: string;
};

function playerName(side: LichessGame['players']['white']): string {
  if (side.user?.name) return side.user.name;
  if (side.name) return side.name;
  if (side.aiLevel != null) return `Stockfish level ${side.aiLevel}`;
  return 'Unknown';
}

function playerId(side: LichessGame['players']['white']): string | null {
  return side.user?.id?.trim() ?? null;
}

export function myColorInLichessGame(game: LichessGame, myUserId: string): ChessPlayerColor | null {
  const whiteId = playerId(game.players.white);
  const blackId = playerId(game.players.black);
  if (whiteId === myUserId) return 'white';
  if (blackId === myUserId) return 'black';
  return null;
}

export function lichessGameOutcome(
  game: LichessGame,
  myColor: ChessPlayerColor | null
): LichessGameOutcome {
  if (!game.winner || !myColor) return 'draw';
  if (game.winner === myColor) return 'win';
  return 'loss';
}

export function formatOutcomeLabel(outcome: LichessGameOutcome): string {
  switch (outcome) {
    case 'win':
      return 'Win';
    case 'loss':
      return 'Loss';
    case 'draw':
      return 'Draw';
  }
}

export function dateIsoFromLichessCreatedAt(createdAt: number): string {
  return new Date(createdAt).toISOString().slice(0, 10);
}

export function summarizeLichessGame(game: LichessGame, myUserId: string): LichessGameSummary {
  const myColor = myColorInLichessGame(game, myUserId);
  const opponentSide = myColor === 'white' ? game.players.black : game.players.white;
  const opponent = playerName(opponentSide);
  const dateIso = dateIsoFromLichessCreatedAt(game.createdAt);
  const outcome = lichessGameOutcome(game, myColor);
  const outcomeLabel = formatOutcomeLabel(outcome);
  const speedLabel = (game.perf ?? game.speed ?? 'game').replace(/_/g, ' ');
  const listLabel = `${dateIso} · vs ${opponent} · ${outcomeLabel} (${speedLabel})`;
  const headline =
    myColor != null
      ? `${dateIso} ${opponent}. ${outcomeLabel}.`
      : `${dateIso}. ${opponent}. ${outcomeLabel}.`;

  return {
    gameId: game.id,
    dateIso,
    opponent,
    outcome,
    outcomeLabel,
    speedLabel,
    myColor,
    listLabel,
    headline,
  };
}
